import { Query, ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { Store } from "modules/customer/insights/portfolioNew/store";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

export class ComparatorCompetitor {
    public readonly comparatorStoreId: string;
    public readonly competitorId: number;
    public readonly storeName: string;
    public readonly brandName: string;
    public readonly primaryCategoryId: number;
    public readonly isAlternateCategory: boolean;
    public readonly alternateCategoryIds: number[];

    constructor(
        comparatorStoreId: string,
        competitorId: number,
        storeName: string,
        brandName: string,
        primaryCategoryId: number,
        isAlternateCategory: boolean,
        alternateCategoryIds: number[]
    ) {
        this.comparatorStoreId = comparatorStoreId;
        this.competitorId = competitorId;
        this.storeName = storeName;
        this.brandName = brandName;
        this.primaryCategoryId = primaryCategoryId;
        this.isAlternateCategory = isAlternateCategory;
        this.alternateCategoryIds = alternateCategoryIds;
    }
}

export class ClassifiedComparatorCompetitor extends ComparatorCompetitor {
    public readonly classification: CompetitorClassification;

    constructor(
        competitor: ComparatorCompetitor,
        classification: CompetitorClassification
    ) {
        super(
            competitor.comparatorStoreId,
            competitor.competitorId,
            competitor.storeName,
            competitor.brandName,
            competitor.primaryCategoryId,
            competitor.isAlternateCategory,
            competitor.alternateCategoryIds
        );
        this.classification = classification;
    }
}

export enum CompetitorClassification {
    Direct = "Direct competitor",
    Standard = "Competitor",
    Alternate = "Alternate Category Competitor",
    Indirect = "Indirect competitor",
}

export const loadComparatorCompetitors = (stores: Store[], directCompetitorNames: string[], placeCategoryIds: number[], accountId: string): AppThunk<Promise<ComparatorCompetitor[]>> => async (dispatch) => {
    try {
        if (stores.length === 0 || placeCategoryIds.length === 0) {
            return [];
        }
        const retailCentreIds = stores.map(store => store.retailCentreID);
        //ToDo: Improve query performance
        const query: Query = {
            dimensions: [
                "Mapping_RC_RZ.RetailCentreID",
                "Competition_DriveTime.PlaceID",
                "Competition_DriveTime.PlaceName",
                "Competition_DriveTime.BrandName",
                "Competition_DriveTime.PlaceCategory_ID",
                "Competition_DriveTime.IsAltPlaceCategory",
                "Competition_DriveTime.ClientIDArray",
                "Competition_DriveTime.AltPlaceCategories"
            ],
            filters: [{
                or: [{
                    member: "Competition_DriveTime.SelectedCategory_ID",
                    operator: "equals",
                    values: placeCategoryIds.map(String)
                }]
            }, {
                member: "Mapping_RC_RZ.RetailCentreID",
                operator: "equals",
                values: retailCentreIds.map(String)
            }]
        };

        if (directCompetitorNames.length > 0) {
            //@ts-ignore
            query.filters[0].or.push({
                member: "Competition_DriveTime.BrandName",
                operator: "equals",
                values: directCompetitorNames
            }, {
                member: "Competition_DriveTime.PlaceName",
                operator: "equals",
                values: directCompetitorNames
            });
        }

        const resultSet = await dispatch(cubeLoad(query)) as unknown as ResultSet;

        const nonClientRawCompetitors = resultSet.rawData()
            .filter(row => !row["Competition_DriveTime.ClientIDArray"] || (JSON.parse(row["Competition_DriveTime.ClientIDArray"]) ?? []).includes(accountId));

        const comparatorCompetitors: ComparatorCompetitor[] = [];
        stores.forEach(store => {
            const relevantData = nonClientRawCompetitors.filter(row => String(row["Mapping_RC_RZ.RetailCentreID"]) === String(store.retailCentreID));
            relevantData.forEach(row => {
                const competitorCount = new ComparatorCompetitor(
                    String(store.id),
                    Number(row["Competition_DriveTime.PlaceID"]),
                    String(row["Competition_DriveTime.PlaceName"]),
                    String(row["Competition_DriveTime.BrandName"]),
                    Number(row["Competition_DriveTime.PlaceCategory_ID"]),
                    Boolean(Number(row["Competition_DriveTime.IsAltPlaceCategory"])),
                    JSON.parse(`[${row["Competition_DriveTime.AltPlaceCategories"] ?? ""}]`)
                );
                comparatorCompetitors.push(competitorCount);
            });
        });
        return comparatorCompetitors;
    } catch (error) {
        dispatch(logError("Error loading Comparator Competitors.", error));
        throw error;
    }
};
